import React from 'react';
import {
  AlertTriangle,
  Archive,
  Download,
  Package,
  RefreshCw,
  ShieldCheck,
  Undo2,
} from 'lucide-react';
import { dashboardService, LogisticsDashboardSummary } from '../../../services/dashboardService';
import { inventoryService, FirearmPayload } from '../../../services/inventoryService';
import { inspectionService } from '../../../services/inspectionService';
import { issuanceService } from '../../../services/issuanceService';
import { notificationService } from '../../../services/notificationService';
import { PaginatedResult } from '../../../services/apiClient';
import { permissionService } from '../../../services/permissionService';
import repairService from '../../../services/repairService';
import { ARE, ChainOfCustodyEvent, Firearm, FirearmRepair, Issuance, MultiIssuanceCase, Notification, ReturnRecord } from '../../../types/domain';
import { FIREARM_EVIDENCE_ALLOWED_MIME_TYPES, FIREARM_EVIDENCE_MAX_BYTES } from '../../../types/firearmEvidence';
import { useAdminDepartmentContext } from '../../../hooks/useAdminDepartmentContext';
import { useReferenceOptions } from '../../../hooks/useReferenceData';
import { withAdminDepartmentParam } from '../../../utils/adminViewMode';
import { todayISO } from '../../../utils/dateValidation';
import { markNotificationReadIfUnread } from '../../../utils/notificationEvents';
import { canonicalStatusKey, canonicalStatusLabel } from '../../../utils/statusPresentation';
import DismissibleError from '../../DismissibleError';
import { ValidationResults } from '../inspector/InspectorWorkspace';
import {
  CompactTableToolbar,
  DetailDrawer,
  DocumentPreviewModal,
  EmptyTableState,
  FirearmEvidencePanel,
  FirearmIssuanceDetailsDrawer,
  FirearmMasterImagePanel,
  FormModal,
  NotificationInbox,
  NotificationInboxItem,
  RecordsTableCard,
  StatusBadge,
  TypeBadge,
  WorkflowFeedback,
} from '../../records';
import CompleteRepairDialog from './CompleteRepairDialog';

const today = todayISO;
const APPROVED_REPORT_TYPES = new Set(['form18', 'inspection_compliance', 'reconciliation']);
const REPORT_TYPE_LABELS: Record<string, string> = {
  form18: 'Form 18',
  inspection_compliance: 'Inspection and Compliance Report',
  reconciliation: 'Reconciliation Report',
};
const CHAIN_WORKFLOW_OPTIONS = [
  { value: 'issuance', label: 'Issuance' },
  { value: 'return', label: 'Return' },
  { value: 'incident', label: 'Incident' },
  { value: 'firearm_record_update', label: 'Firearm Record Update' },
  { value: 'officer_record_update', label: 'Officer Record Update' },
  { value: 'are_upload', label: 'ARE Upload' },
  { value: 'firearm_image_upload', label: 'Firearm Image Upload' },
];
const CHAIN_ACTION_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'status_changed', label: 'Status Changed' },
  { value: 'document_uploaded', label: 'Document Uploaded' },
  { value: 'image_uploaded', label: 'Image Uploaded' },
  { value: 'record_updated', label: 'Record Updated' },
  { value: 'delete_attempted', label: 'Delete Attempted' },
];

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const statusText = (status?: string) => value(status).replace(/_/g, ' ');
const titleText = (status?: string): string =>
  value(status).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const canonicalFirearmStatus = (status?: string): string => ({
  in_storage: 'available',
  issued: 'issued',
  returned: 'returned',
  unserviceable: 'for_repair',
  beyond_economical_repair: 'inactive',
  lost: 'inactive',
  stolen: 'inactive',
  archived: 'archived',
}[status || ''] || status || '');
const canonicalFirearmStatusLabel = (status?: string): string => ({
  in_storage: 'Available',
  issued: 'Issued',
  returned: 'Returned',
  unserviceable: 'For Repair',
  beyond_economical_repair: 'Inactive',
  lost: 'Inactive',
  stolen: 'Inactive',
  archived: 'Archived',
}[status || ''] || titleText(status));
const incidentStatusLabel = (status?: string): string => {
  if (status === 'under_investigation') return 'Pending Investigation';
  if (status === 'closed') return 'Incident Closed';
  return titleText(status);
};
const textMatches = (parts: unknown[], query: string): boolean =>
  parts.map((part) => value(part, '')).join(' ').toLowerCase().includes(query.toLowerCase());
const formatFieldValue = (input: unknown): string => {
  if (Array.isArray(input)) return input.map(formatFieldValue).join(' ');
  if (input && typeof input === 'object') {
    return Object.entries(input)
      .map(([key, nestedValue]) => `${key}: ${formatFieldValue(nestedValue)}`)
      .join('; ');
  }
  return value(input, '');
};
const fieldLabel = (field: string): string =>
  field === 'non_field_errors' ? 'Non-field errors' : field;
const errorText = (err: any, fallback: string): string => {
  const data = err?.response?.data || err?.originalError?.response?.data;
  if (data?.code === 'authorization_document_integrity_failed' || data?.authorization_document?.code === 'authorization_document_integrity_failed') {
    return 'The authorization document failed integrity verification. Do not approve this request. Contact an administrator.';
  }
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const fieldMessages = Object.entries(data)
      .filter(([key]) => key !== 'error' && key !== 'detail')
      .map(([key, fieldValue]) => `${fieldLabel(key)}: ${formatFieldValue(fieldValue)}`)
      .filter((message) => !message.endsWith(': '));
    if (fieldMessages.length > 0) return fieldMessages.join('; ');
    if (data.detail) return formatFieldValue(data.detail);
    if (data.error && !/invalid data provided/i.test(String(data.error))) return formatFieldValue(data.error);
  }
  return err?.message || fallback;
};
const areDocumentName = (are: ARE): string =>
  value(are.document_original_name || (are.scanned_document ? are.scanned_document.split('/').pop() : ''), '');
const isAreUploaded = (are: ARE): boolean =>
  Boolean(are.expiration_date && (are.document_original_name || are.scanned_document || are.document));
const areUploadStatus = (are: ARE): string => {
  if (value(are.status, '').toLowerCase() === 'rejected') return 'rejected';
  if (isAreUploaded(are)) return 'are_uploaded';
  if (are.document_original_name || are.scanned_document || are.document) return 'missing_expiration';
  return 'pending_accountable_are';
};
const areUploadStatusLabel = (are: ARE): string => {
  const status = areUploadStatus(are);
  if (status === 'are_uploaded') return 'ARE Uploaded';
  if (status === 'missing_expiration') return 'Missing Expiration';
  if (status === 'rejected') return 'Rejected';
  return 'Pending Accountable ARE';
};
const areUploadTone = (are: ARE): 'success' | 'warning' | 'danger' => {
  const status = areUploadStatus(are);
  if (status === 'are_uploaded') return 'success';
  if (status === 'rejected') return 'danger';
  return 'warning';
};
const areExpirationLabel = (are: ARE): string => {
  if (are.expires_today) return 'Expires Today';
  return titleText(are.expiration_status || 'missing_expiration');
};
const areExpirationTone = (are: ARE): 'success' | 'warning' | 'danger' => {
  if (are.expiration_status === 'expired') return 'danger';
  if (are.expiration_status === 'valid') return 'success';
  return 'warning';
};
const areDocumentPreviewLabel = (are: ARE): string => {
  const filename = areDocumentName(are).toLowerCase();
  const mimeType = (are.document_mime_type || '').toLowerCase();
  if (mimeType.includes('pdf') || filename.endsWith('.pdf')) return 'View PDF';
  if (mimeType.startsWith('image/') || /\.(jpe?g)$/.test(filename)) return 'View Image';
  return 'View Document';
};

interface AREDocumentPreviewState {
  areId: number;
  filename: string;
  objectUrl?: string;
  contentType?: string;
  loading: boolean;
  error?: string;
}
const deploymentText = (firearm: Firearm): string => {
  if (firearm.department_name || firearm.assigned_to_name) {
    return value(firearm.department_name || firearm.assigned_to_name);
  }
  if (firearm.status === 'in_storage') return canonicalStatusLabel(firearm);
  return 'Unassigned';
};

// Invisible implementation note for FRS traceability.
// Intentionally returns null so these notes do not render in the UI.
const FRSNote: React.FC<{ children: React.ReactNode }> = () => null;

const EmptyState: React.FC<{ title: string; copy: string }> = ({ title, copy }) => (
  <div className="workspace-empty">
    <Package size={22} />
    <strong>{title}</strong>
    <p>{copy}</p>
  </div>
);

const has = (permission: string): boolean => permissionService.hasPermission(permission);

const formatDate = (date?: string | null): string => {
  if (!date) return '-';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString();
};

const formatDateTime = (date?: string | null): string => {
  if (!date) return '-';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const COMPLIANCE_LABELS: Record<string, string> = {
  are_expiring: 'ARE Expiring Soon',
  are_expiry: 'ARE Expiring Soon',
  are_expiring_soon: 'ARE Expiring Soon',
  are_expired: 'ARE Expired',
  are_expiration_missing: 'Missing ARE Expiration',
  ltopf_expiring: 'LTOPF Expiring Soon',
  ltopf_expiry: 'LTOPF Expiring Soon',
  ltopf_expiring_soon: 'LTOPF Expiring Soon',
  ltopf_expired: 'LTOPF Expired',
  return_due: 'Return Due',
  incident: 'Incident Alert',
  incident_report_discrepancy: 'Missing Incident Report',
  unrecorded_firearm: 'Firearm Not Found in Registry',
  new_discovery: 'Firearm Not Found in Registry',
  conflict: 'Officer Information Mismatch',
  incident_status_mismatch: 'Incident Status Mismatch',
  resolved_discrepancy: 'Discrepancy Resolved',
  compliance_alert: 'Compliance Alert',
  system: 'System',
};

const complianceLabel = (type?: string): string =>
  COMPLIANCE_LABELS[type || ''] || titleText(type || 'Compliance Alert');

const TECHNICAL_NOTIFICATION_KEYS = Object.keys(COMPLIANCE_LABELS)
  .filter((key) => key !== 'system' && key !== 'incident')
  .sort((a, b) => b.length - a.length);

const sanitizeNotificationText = (input?: string | null): string => {
  if (!input) return '';
  return TECHNICAL_NOTIFICATION_KEYS.reduce((text, key) => (
    text.replace(new RegExp(`\\b${key}\\b`, 'gi'), COMPLIANCE_LABELS[key])
  ), input);
};

const notificationTitle = (notification: any): string => {
  if (notification.inspection_outcome) {
    return notification.inspection_outcome.is_verified === true
      ? 'Validation Passed'
      : notification.inspection_outcome.label || 'Inspection Validation Notification';
  }
  if (notification.display_type) return notification.display_type;
  const friendly = complianceLabel(notification.notification_type || notification.type);
  return friendly || sanitizeNotificationText(notification.title) || 'Compliance Alert';
};

const notificationMessage = (notification: any): string =>
  sanitizeNotificationText(notification.message)
    || `${notificationTitle(notification)} requires logistics review.`;

const complianceContext = (notification: any): string => {
  const parts = [
    notification.firearm_serial || notification.firearm_serial_number || notification.serial_number,
    notification.officer_name || notification.officer_badge,
    notification.department_name || notification.department || notification.unit,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : 'No related record listed';
};

const inactiveIssuanceStatuses = new Set([
  'returned',
  'rejected',
  'cancelled',
  'canceled',
  'archived',
  'inactive',
  'revoked',
  'expired',
  'incident_closed',
  'multi_issuance_resolved',
]);

const closedReturnStatuses = new Set(['returned', 'revoked', 'completed', 'cancelled', 'canceled', 'archived', 'inactive', 'incident_closed']);

const normalizedStatus = (status?: string | null): string => value(status, '').trim().toLowerCase();

const issuanceStatusValue = (issuance: Issuance): string =>
  normalizedStatus(issuance.status || issuance.status_display);

const isVisibleActiveIssuance = (issuance: Issuance): boolean => {
  const status = issuanceStatusValue(issuance);
  const closureType = normalizedStatus(issuance.closure_type);
  if (inactiveIssuanceStatuses.has(status) || inactiveIssuanceStatuses.has(closureType)) return false;
  if (issuance.returned_at) return false;
  return true;
};

const isVisibleReturnIssuance = (issuance: Issuance): boolean => {
  const status = issuanceStatusValue(issuance);
  const closureType = normalizedStatus(issuance.closure_type);
  if (closedReturnStatuses.has(status) || closedReturnStatuses.has(closureType)) return false;
  if (issuance.returned_at) return false;
  return true;
};

const issuanceDepartment = (issuance: Issuance): string =>
  value((issuance as any).department_name || (issuance as any).officer_unit || issuance.are_record?.officer_unit, '');

const issuanceOfficerText = (issuance: Issuance): string =>
  value(issuance.officer_name || issuance.officer_badge || issuance.are_record?.officer_name || issuance.are_record?.officer_badge_number, '');

const hasMultiIssuance = (issuance: Issuance): boolean =>
  Boolean(issuance.multi_issuance?.detected);

const isResolvedMultiIssuance = (issuance: Issuance): boolean =>
  hasMultiIssuance(issuance)
  && (
    Boolean(issuance.multi_issuance?.resolved)
    || normalizedStatus(issuance.status) === 'multi_issuance_resolved'
  );

const multiIssuanceSummary = (issuance: Issuance): string => {
  const multi = issuance.multi_issuance;
  if (!multi?.detected) return '';
  if (isResolvedMultiIssuance(issuance) || issuance.status === 'rejected') return 'Closed due to Multi-Issuance';
  const conflictTypes = new Set(multi.conflict_types || []);
  if (conflictTypes.has('same_firearm_multiple_officers')) return 'Same firearm conflict';
  return 'Conflict — Multi-Issuance';
};

const multiIssuanceStatusText = (issuance: Issuance): string => {
  const multi = issuance.multi_issuance;
  if (!multi?.detected) return '-';
  if (multi.resolved) return 'Resolved';
  if (multi.flagged || issuance.status === 'multi_issuance') return 'Flagged';
  return 'Detected';
};

const conflictTypeLabel = (type: string): string => {
  if (type === 'same_firearm_multiple_officers') return 'Same firearm conflict';
  return titleText(type);
};

const multiIssuanceDetailRows = (issuance: Issuance): Array<[string, string]> => {
  const multi = issuance.multi_issuance || {};
  const details = multi.details || {};
  const sameFirearm = details.same_firearm_multiple_officers || {};
  return [
    ['Current issuance status', titleText(issuance.status_display || issuance.status)],
    ['Multi-Issuance status', multiIssuanceStatusText(issuance)],
    ['Conflict type', (multi.conflict_types || []).map(conflictTypeLabel).join(', ')],
    ['Firearm serial', sameFirearm.firearm_serial_number || issuance.firearm_serial],
    ['Current holder', sameFirearm.current_officer_name],
    ['Attempted holder', sameFirearm.attempted_officer_name || issuance.officer_name],
    ['Blocking issuance ID', sameFirearm.blocking_issuance_id],
    ['Resolved by', value(multi.resolved_by, '')],
    ['Resolved at', formatDateTime(multi.resolved_at)],
    ['Resolution method', titleText(multi.resolution_method)],
    ['Resolution message', multi.resolution_message],
    ['Resolution notes', multi.resolution_notes],
    ['Original system message', sameFirearm.message],
  ].filter(([, detail]) => Boolean(detail && detail !== '-')) as Array<[string, string]>;
};

const issuanceFirearm = (issuance: Issuance): string =>
  value(issuance.firearm_serial || issuance.are_record?.firearm_serial_number || issuance.firearm_model_display || issuance.are_record?.firearm_model, '');

const badgeTone = (value?: string): 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'purple' => {
  const normalized = (value || '').toLowerCase();
  if (normalized.includes('critical') || normalized.includes('high') || normalized.includes('lost') || normalized.includes('stolen')) return 'red';
  if (normalized.includes('warning') || normalized.includes('medium') || normalized.includes('due') || normalized.includes('pending')) return 'amber';
  if (normalized.includes('low') || normalized.includes('resolved') || normalized.includes('verified')) return 'green';
  if (normalized.includes('incident') || normalized.includes('conflict') || normalized.includes('mismatch')) return 'purple';
  return 'blue';
};

const firearmInitialState: FirearmPayload = {
  article: '',
  description: '',
  model: '',
  serial_number: '',
  caliber: '',
  servicability: 'serviceable',
  acquisition_date: '',
  status: 'in_storage',
};

const buildFirearmFormData = (form: FirearmPayload, image: File | null): FormData => {
  const data = new FormData();
  Object.entries(form).forEach(([key, fieldValue]) => {
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      data.append(key, String(fieldValue));
    }
  });
  if (image instanceof File) {
    data.append('firearm_image', image, image.name);
  }
  return data;
};

const firearmCreateErrorText = (err: any): string => {
  const status = err?.statusCode || err?.response?.status || err?.originalError?.response?.status;
  if (status === 413) {
    return 'The selected firearm image is too large to upload. Select an image that does not exceed 5 MB.';
  }
  const data = err?.response?.data || err?.originalError?.response?.data;
  const imageError = data?.firearm_image;
  if (imageError) {
    const message = formatFieldValue(imageError);
    if (/not a file|encoding type/i.test(message)) {
      return 'The selected firearm image could not be uploaded. Please select the image again.';
    }
    return `Firearm image: ${message}`;
  }
  return errorText(err, 'The firearm record could not be created. Review the form and try again.');
};

export const LogisticsDashboard: React.FC = () => {
  // FRS 2.1-2.4 and FRS 8.1-8.3: inventory summary, status distribution, discrepancy alerts, activity, compliance signals.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const [summary, setSummary] = React.useState<LogisticsDashboardSummary | null>(null);
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const nextSummary = await dashboardService.getLogisticsSummary(adminFilter);
      setSummary(nextSummary);
      if (has('notification.system.view')) {
        setNotifications(await notificationService.getSystemNotifications(adminFilter));
      }
    } catch {
      setError('Logistics dashboard summary is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [adminFilter]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading) return <div className="loading-state"><span className="loading-mark" />Loading logistics dashboard...</div>;
  if (error) return <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />;
  if (!summary) return <EmptyState title="No dashboard data" copy="Dashboard summary returned no records." />;

  const metrics = [
    ['Total registered firearms', summary.total_registered_firearms, 'Registry', Package, 'blue'],
    ['Issued / Active Duty', summary.status_distribution.issued_active_duty, 'Active assignments', ShieldCheck, 'green'],
    ['Returned / In Storage', summary.status_distribution.returned_in_storage, 'Storage', Archive, 'purple'],
    ['Pending returns', summary.pending_returns, 'Return workflow', Undo2, 'neutral'],
    ['Compliance alerts', summary.compliance_alerts.length, 'Notifications', AlertTriangle, 'neutral'],
  ] as const;

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div>
          <h1>Logistics Dashboard</h1>
          <p>Central logistics overview for firearm status, returns, and compliance.</p>
        </div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} />Refresh data</button>
      </div>

      <div className="stats-grid inspector-stats">
        {metrics.map(([label, metric, note, Icon, tone]) => (
          <article className="stat-card" key={label}>
            <span className={`metric-icon ${tone}`}><Icon size={20} /></span>
            <div>
              <span className="stat-label">{label}</span>
              <strong className="stat-number">{metric}</strong>
              <span className="stat-note">{note}</span>
            </div>
          </article>
        ))}
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Status distribution</h2>
              <FRSNote>Logistics dashboard: registered firearms by operational status.</FRSNote>
            </div>
          </div>
          <div className="health-list">
            <div className="health-row"><span>Issued / Active Duty</span><strong>{summary.status_distribution.issued_active_duty}</strong></div>
            <div className="health-row"><span>Returned / In Storage</span><strong>{summary.status_distribution.returned_in_storage}</strong></div>
            <div className="health-row"><span>For Repair / Unserviceable</span><strong>{summary.status_distribution.for_repair_unserviceable}</strong></div>
            <div className="health-row"><span>Lost / Pending Investigation</span><strong>{summary.status_distribution.lost_under_investigation}</strong></div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Pending returns</h2>
              <FRSNote>Logistics dashboard: returns awaiting workflow attention.</FRSNote>
            </div>
          </div>
          <div className="workspace-empty"><Archive size={22} /><strong>{summary.pending_returns}</strong><p>Pending return record(s) currently require review.</p></div>
        </section>

        {has('notification.system.view') && (
          <section className="panel">
            <div className="panel-header">
              <div>
                <h2>Compliance alerts</h2>
                <FRSNote>FRS 8.1-8.3: LTOPF/ARE renewal and non-compliance notifications for Logistics.</FRSNote>
              </div>
            </div>
            {(summary.compliance_alerts.length || notifications.length) === 0 ? (
              <EmptyState title="No compliance alerts" copy="No active logistics compliance notifications were returned." />
            ) : (
              <div className="alert-preview-list">
                {(notifications.length ? notifications : summary.compliance_alerts).slice(0, 5).map((notification: any, index) => {
                  const type = notification.notification_type || notification.type;
                  const title = notificationTitle(notification);
                  const message = notificationMessage(notification);
                  const date = notification.due_date || notification.generated_at || notification.created_at;
                  const badgeValue = notification.severity || notification.status || type || 'compliance';
                  return (
                    <article className="alert-preview-item" key={notification.id || `${type}-${index}`}>
                      <div className="alert-preview-main">
                        <div className="alert-preview-title">
                          <strong>{title}</strong>
                          <TypeBadge type={badgeValue} label={titleText(badgeValue)} tone={badgeTone(badgeValue)} />
                        </div>
                        <p>{message}</p>
                        <div className="alert-preview-meta">
                          <span>{complianceContext(notification)}</span>
                          {date ? <span>{notification.due_date ? `Due ${formatDate(date)}` : formatDate(date)}</span> : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Recent custody events</h2>
              <FRSNote>Logistics dashboard: latest canonical chain-of-custody events.</FRSNote>
            </div>
          </div>
          {!summary.recent_custody_events?.length ? (
            <EmptyState title="No custody events" copy="No recent custody events were returned." />
          ) : (
            <div className="health-list">
              {summary.recent_custody_events.slice(0, 5).map((event: any) => (
                <div className="health-row" key={event.id}>
                  <span>{value(event.firearm_serial)}</span>
                  <strong>{statusText(event.action_type)}</strong>
                  <small>{formatDate(event.created_at)}</small>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export const FirearmRegistry: React.FC = () => {
  // FRS 3.1-3.2: searchable registry with authorized add/edit only for firearm.inventory.manage.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const canManage = has('firearm.inventory.manage');
  const [firearms, setFirearms] = React.useState<Firearm[]>([]);
  const [form, setForm] = React.useState<FirearmPayload>(firearmInitialState);
  const [editing, setEditing] = React.useState<Firearm | null>(null);
  const [selectedDetails, setSelectedDetails] = React.useState<Firearm | null>(null);
  const [firearmImage, setFirearmImage] = React.useState<File | null>(null);
  const [firearmImagePreview, setFirearmImagePreview] = React.useState<string | null>(null);
  const firearmImageInputRef = React.useRef<HTMLInputElement | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [articleFilter, setArticleFilter] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const { options: firearmTypeOptions } = useReferenceOptions('firearm_types');
  const { options: firearmConditionOptions } = useReferenceOptions('firearm_conditions');
  const { options: firearmStatusOptions } = useReferenceOptions('firearm_statuses');
  const supportedFirearmArticles = React.useMemo(
    () => firearmTypeOptions.map((option) => option.label),
    [firearmTypeOptions],
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setFirearms(await inventoryService.getFirearms(withAdminDepartmentParam({
        search,
        article: articleFilter || undefined,
        status,
      }, adminDepartmentId)));
    } catch {
      setError('Firearm inventory is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [adminDepartmentId, articleFilter, search, status]);

  React.useEffect(() => { void load(); }, [load]);

  React.useEffect(() => () => {
    if (firearmImagePreview) URL.revokeObjectURL(firearmImagePreview);
  }, [firearmImagePreview]);

  const clearSelectedImage = React.useCallback(() => {
    setFirearmImage(null);
    setFirearmImagePreview(null);
    if (firearmImageInputRef.current) firearmImageInputRef.current.value = '';
  }, []);

  const selectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    if (!file) {
      clearSelectedImage();
      return;
    }
    if (!FIREARM_EVIDENCE_ALLOWED_MIME_TYPES.includes(file.type as any)) {
      setError('The selected file is not a supported image. Use JPEG, PNG, or WebP.');
      clearSelectedImage();
      return;
    }
    if (file.size > FIREARM_EVIDENCE_MAX_BYTES) {
      setError('The firearm image exceeds the maximum allowed size of 5 MB.');
      clearSelectedImage();
      return;
    }
    setError('');
    setFirearmImage(file);
    setFirearmImagePreview(URL.createObjectURL(file));
  };

  const filtered = firearms.filter((firearm) => {
    const statusMatch = !status || firearm.status_group === status || canonicalFirearmStatus(firearm.status) === status;
    const searchMatch = !search || textMatches([
      firearm.serial_number,
      firearm.article,
      firearm.description,
      firearm.model,
      firearm.assigned_to_name,
      firearm.assigned_to_badge,
      firearm.department_name,
    ], search);
    return searchMatch && statusMatch;
  });

  const validate = (): string => {
    if (!form.article.trim()) return 'Article required.';
    const editingUnsupportedLegacy = Boolean(
      editing && editing.is_supported_for_issuance === false && form.article === editing.article
    );
    if (!editingUnsupportedLegacy && !supportedFirearmArticles.includes(form.article)) {
      return 'Select Pistol or Revolver.';
    }
    if (!form.description.trim() || !form.model.trim()) return 'Description/Make and Model required.';
    if (!form.serial_number.trim()) return 'Serial Number required.';
    const duplicate = firearms.some((firearm) =>
      firearm.serial_number.toLowerCase() === form.serial_number.trim().toLowerCase() && firearm.id !== editing?.id
    );
    if (duplicate) return 'Serial Number must be unique.';
    if (!form.acquisition_date) return 'Date of Acquisition required.';
    if (form.acquisition_date > today()) return 'Acquisition date cannot be later than today.';
    if (!form.status) return 'Current Status required.';
    return '';
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setMessage('');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await inventoryService.updateFirearm(editing.id, form);
        setMessage('Firearm record updated.');
      } else {
        await inventoryService.createFirearm(buildFirearmFormData(form, firearmImage));
        setMessage('Firearm record created.');
      }
      setError('');
      setForm(firearmInitialState);
      setEditing(null);
      clearSelectedImage();
      setDrawerOpen(false);
      await load();
    } catch (err: any) {
      setMessage('');
      setError(editing
        ? errorText(err, 'Firearm record could not be saved.')
        : firearmCreateErrorText(err));
    } finally {
      setSubmitting(false);
    }
  };

  const edit = (firearm: Firearm) => {
    setEditing(firearm);
    setForm({
      article: firearm.article || '',
      description: firearm.description || '',
      model: firearm.model || '',
      serial_number: firearm.serial_number,
      caliber: firearm.caliber || '',
      servicability: firearm.servicability || 'serviceable',
      acquisition_date: firearm.acquisition_date || '',
      status: firearm.status || 'in_storage',
    });
    setError('');
    setMessage('');
    clearSelectedImage();
    setDrawerOpen(true);
  };

  const openCreateDrawer = () => {
    setEditing(null);
    setForm(firearmInitialState);
    setError('');
    setMessage('');
    clearSelectedImage();
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setForm(firearmInitialState);
    setError('');
    clearSelectedImage();
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div>
          <h1>Firearm Registry</h1>
          <p>Firearm master records for authorized Logistics registry maintenance.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={load}><RefreshCw size={14} />Refresh</button>
      </div>
      <FRSNote>FRS 3.1-3.2: Register firearm, edit firearm master data, and view firearm details only. Issuance, return, and custody actions are intentionally not exposed here.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && !drawerOpen && <DismissibleError message={error} onDismiss={() => setError('')} />}

      <RecordsTableCard
        title="Firearm Registry Records"
        description="Search, scan, and maintain logistics firearm master records."
        meta={loading ? 'Loading' : `${filtered.length} of ${firearms.length}`}
        actions={canManage ? <button className="btn btn-primary" type="button" onClick={openCreateDrawer}>+ Add Firearm Record</button> : null}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="Search serial, model, officer"
            onSearchChange={setSearch}
            filters={(
              <>
                <select aria-label="Article filter" value={articleFilter} onChange={(event) => setArticleFilter(event.target.value)}>
                  <option value="">All Articles</option>
                  {firearmTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select aria-label="Status filter" value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">All Statuses</option>
                  {firearmStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </>
            )}
            resultCount={loading ? undefined : `${filtered.length} result${filtered.length === 1 ? '' : 's'}`}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Serial</th><th>Article</th><th>Description / Model</th><th>Deployment</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}><div className="loading-state"><span className="loading-mark" />Loading inventory...</div></td></tr>
            ) : error && !drawerOpen ? (
              <tr><td colSpan={6}><DismissibleError message={error} onDismiss={() => setError('')} /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6}><EmptyTableState title="No firearm records" description="No firearm records matched the current filters." /></td></tr>
            ) : filtered.map((firearm) => (
              <tr key={firearm.id}>
                <td>{firearm.serial_number}</td>
                <td>{value(firearm.article)}</td>
                <td>{value(firearm.description)} / {value(firearm.model)}</td>
                <td>{deploymentText(firearm)}</td>
                <td><StatusBadge status={canonicalStatusKey(firearm)} label={canonicalStatusLabel(firearm)} /></td>
                <td><div className="table-actions"><button className="btn btn-secondary" type="button" onClick={() => setSelectedDetails(firearm)}>View Details</button>{canManage ? <button className="btn btn-secondary" type="button" onClick={() => edit(firearm)}>Edit firearm record</button> : null}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>

      {canManage && (
        <FormModal
          open={drawerOpen}
          title={editing ? 'Edit firearm record' : 'Add firearm record'}
          description="Maintain firearm master record details only."
          onClose={closeDrawer}
          closeLabel="Close firearm record form"
          size="lg"
        >
          {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
          <form className="inspector-form" onSubmit={submit}>
          <FRSNote>FRS 3.2: only users with firearm.inventory.manage can add or edit registry records.</FRSNote>
          <label>Article<select value={form.article} onChange={(event) => setForm({ ...form, article: event.target.value })}><option value="">Select supported category</option>{editing && form.article && !supportedFirearmArticles.includes(form.article) && <option value={form.article} disabled>{form.article} (unsupported legacy)</option>}{firearmTypeOptions.map((option) => <option key={option.value} value={option.label}>{option.label}</option>)}</select></label>
          <label>Description / Make<input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
          <label>Model<input value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} /></label>
          <label>Serial Number<input value={form.serial_number} onChange={(event) => setForm({ ...form, serial_number: event.target.value })} /></label>
          <label>Caliber<input value={form.caliber} onChange={(event) => setForm({ ...form, caliber: event.target.value })} /></label>
          <label>Date of Acquisition<input type="date" max={today()} value={form.acquisition_date} onChange={(event) => setForm({ ...form, acquisition_date: event.target.value })} /></label>
          <label>Current Firearm Condition<select value={form.servicability} onChange={(event) => setForm({ ...form, servicability: event.target.value })}>{firearmConditionOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label>Firearm Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="">Select status</option><option value="in_storage">Available</option><option value="issued">Issued</option><option value="unserviceable">For Repair</option><option value="beyond_economical_repair">Inactive</option></select></label>
          {!editing && <label>Firearm Image (optional)<input ref={firearmImageInputRef} aria-label="Firearm Image" name="firearm_image" type="file" accept="image/jpeg,image/png,image/webp" onChange={selectImage} /><small>JPEG, PNG, or WebP. Maximum 5 MB.</small></label>}
          {!editing && firearmImage && firearmImagePreview && <div className="selected-firearm-image full-width"><img className="firearm-upload-preview" src={firearmImagePreview} alt="Selected firearm preview" /><div className="selected-firearm-image-metadata"><strong>{firearmImage.name}</strong><span>{(firearmImage.size / 1024 / 1024).toFixed(2)} MB</span></div><div><button className="btn btn-secondary" type="button" onClick={clearSelectedImage}>Remove selected image</button></div></div>}
          <button className="btn btn-primary" type="submit" disabled={submitting}>{submitting ? 'Saving...' : editing ? 'Save firearm record' : 'Add firearm record'}</button>
          <button className="btn btn-secondary" type="button" onClick={closeDrawer} disabled={submitting}>Cancel</button>
        </form>
        </FormModal>
      )}
      {selectedDetails && <DetailDrawer open title="Firearm Details" subtitle={`Registry record ${selectedDetails.serial_number}`} className="firearm-registry-details-drawer" onClose={() => setSelectedDetails(null)}>
        <div className="firearm-detail-layout">
          <section className="firearm-detail-summary" aria-label="Firearm registry information">
            <div className="firearm-detail-section-heading">
              <div><span>Registry information</span><h3>Master record</h3></div>
              <StatusBadge status={selectedDetails.status} label={titleText(selectedDetails.status)} />
            </div>
            <div className="detail-grid">
              <div><span>Serial number</span><strong>{selectedDetails.serial_number}</strong></div>
              <div><span>Article</span><strong>{value(selectedDetails.article)}</strong></div>
              <div><span>Description / Make</span><strong>{value(selectedDetails.description)}</strong></div>
              <div><span>Model</span><strong>{value(selectedDetails.model)}</strong></div>
              <div><span>Caliber</span><strong>{value(selectedDetails.caliber)}</strong></div>
              <div><span>Acquisition date</span><strong>{formatDate(selectedDetails.acquisition_date)}</strong></div>
              <div className="full-width"><span>Department</span><strong>{value(selectedDetails.department_name)}</strong></div>
            </div>
          </section>
          <FirearmMasterImagePanel image={selectedDetails.primary_image} firearmSerialNumber={selectedDetails.serial_number} />
        </div>
      </DetailDrawer>}
    </div>
  );
};

export const CustodyHistory: React.FC = () => {
  // FRS 3.3: read-only chain of custody history.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const [logs, setLogs] = React.useState<ChainOfCustodyEvent[]>([]);
  const [selectedLog, setSelectedLog] = React.useState<ChainOfCustodyEvent | null>(null);
  const [search, setSearch] = React.useState('');
  const [workflowFilter, setWorkflowFilter] = React.useState('');
  const [actionFilter, setActionFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [dateFilter, setDateFilter] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const { options: firearmStatuses } = useReferenceOptions('firearm_statuses');
  const { options: issuanceStatuses } = useReferenceOptions('issuance_statuses');
  const { options: returnStatuses } = useReferenceOptions('return_statuses');
  const { options: incidentStatuses } = useReferenceOptions('incident_statuses');
  const { options: areStatuses } = useReferenceOptions('are_statuses');
  const statusOptions = React.useMemo(() => {
    const byLabel = new Map<string, { value: string; label: string }>();
    [...firearmStatuses, ...issuanceStatuses, ...returnStatuses, ...incidentStatuses, ...areStatuses]
      .forEach((option) => byLabel.set(option.label, option));
    return Array.from(byLabel.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [areStatuses, firearmStatuses, incidentStatuses, issuanceStatuses, returnStatuses]);

  React.useEffect(() => {
    inventoryService.getCustodyLogs(adminFilter)
      .then(setLogs)
      .catch(() => setError('Custody history is temporarily unavailable.'))
      .finally(() => setLoading(false));
  }, [adminFilter]);

  const workflowLabel = (log: ChainOfCustodyEvent) => value(log.workflow_type_display || titleText(log.workflow_type), '');
  const actionLabel = (log: ChainOfCustodyEvent) => value(log.action_type_display || titleText(log.action_type), '');
  const actorLabel = (log: ChainOfCustodyEvent, prefix: 'performed_by' | 'reviewed_by') => {
    const fullName = value((log as any)[`${prefix}_full_name`] || (log as any)[`${prefix}_username`], '');
    const role = value((log as any)[`${prefix}_role_display`] || (log as any)[`${prefix}_role_name`], 'Unspecified Role');
    if (!fullName) return prefix === 'reviewed_by' ? 'Not reviewed' : `Unknown User - ${role}`;
    return `${fullName} - ${role}`;
  };
  const officerLabel = (log: ChainOfCustodyEvent) => value(log.officer_name || log.officer_badge, 'No officer linked.');
  const humanizeKnownValue = (input: unknown): string => {
    if (input === null || input === undefined || input === '') return '-';
    if (typeof input === 'boolean') return input ? 'Yes' : 'No';
    if (typeof input === 'number') return String(input);
    const raw = String(input);
    const known: Record<string, string> = {
      accountable_officer_management: 'Accountable Officer Management',
      logistics_officer_management: 'Logistics Officer Management',
      firearmissuance: 'Firearm Issuance',
      firearmreturnrecord: 'Firearm Return Record',
      firearmincident: 'Firearm Incident',
      chainofcustodylog: 'Chain of Custody Log',
      issued: 'Issued',
      submitted: 'Submitted',
      rejected: 'Rejected',
      confirmed: 'Confirmed',
      in_storage: 'In Storage',
      serviceable: 'Serviceable',
      unserviceable: 'Unserviceable',
      beyond_economical_repair: 'Damaged',
    };
    if (known[raw]) return known[raw];
    if (/^[a-z0-9_]+$/.test(raw) && raw.includes('_')) return titleText(raw);
    return raw;
  };
  const humanizeKey = (key: string): string => {
    const known: Record<string, string> = {
      assigned_to: 'Assigned Officer ID',
      firearm_status: 'Firearm Status',
      issuance_status: 'Issuance Status',
      firearm_servicability: 'Firearm Serviceability',
      rejection_reason: 'Rejection Reason',
      logistics_officer_id: 'Logistics Officer ID',
      accountable_officer_id: 'Accountable Officer ID',
      officer_badge_number: 'Officer Badge Number',
      firearm_serial_number: 'Firearm Serial Number',
      return_status: 'Return Status',
      return_record_id: 'Return Record ID',
      issuance_id: 'Issuance ID',
      is_successful: 'Successful',
    };
    return known[key] || titleText(key);
  };
  const readablePairs = (input?: Record<string, any>) => {
    if (!input || Object.keys(input).length === 0) return [];
    return Object.entries(input).map(([key, nextValue]) => ({
      label: humanizeKey(key),
      value: nextValue && typeof nextValue === 'object'
        ? JSON.stringify(nextValue)
        : humanizeKnownValue(nextValue),
    }));
  };
  const readableBlock = (input?: Record<string, any>, emptyMessage = 'No data recorded') => {
    const pairs = readablePairs(input);
    if (pairs.length === 0) return emptyMessage;
    return pairs.map(({ label, value: pairValue }) => `${label}: ${pairValue}`).join('; ');
  };
  const KeyValueSection: React.FC<{ title: string; empty: string; data?: Record<string, any> }> = ({ title, empty, data }) => {
    const pairs = readablePairs(data);
    return (
      <section className="custody-readable-section">
        <h3>{title}</h3>
        {pairs.length === 0 ? <p className="records-table-muted">{empty}</p> : (
          <dl>
            {pairs.map((pair) => (
              <div key={pair.label}>
                <dt>{pair.label}</dt>
                <dd>{pair.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    );
  };
  const custodySummary = (log: ChainOfCustodyEvent) => {
    const firearm = value(log.firearm_serial, 'This firearm');
    const action = actionLabel(log).toLowerCase();
    const workflow = workflowLabel(log);
    const actor = actorLabel(log, 'performed_by');
    const date = formatDateTime(log.created_at || log.timestamp);
    return `Firearm ${firearm} was ${action} in the ${workflow} workflow by ${actor} on ${date}.`;
  };
  const resultingStatus = (log: ChainOfCustodyEvent) => {
    const state = log.new_state || {};
    const rawStatus = value(
      state.firearm_status_display
        || state.firearm_status
        || state.status_display
        || state.status
        || state.custody_status
        || state.assigned_to_display,
      'No status change recorded',
    );
    return canonicalFirearmStatusLabel(rawStatus);
  };

  const visibleLogs = logs.filter((log) => {
    const workflow = workflowLabel(log);
    const action = actionLabel(log);
    const status = resultingStatus(log);
    const date = value(log.created_at || log.timestamp, '');
    return textMatches([
      log.firearm_serial,
      log.officer_name,
      log.officer_badge,
      workflow,
      action,
      actorLabel(log, 'performed_by'),
      actorLabel(log, 'reviewed_by'),
      status,
    ], search)
      && (!workflowFilter || log.workflow_type === workflowFilter)
      && (!actionFilter || log.action_type === actionFilter)
      && (!statusFilter || status.toLowerCase().includes(statusFilter.toLowerCase()))
      && (!dateFilter || date.startsWith(dateFilter));
  });

  const exportCsv = () => {
    const header = [
      'Firearm serial', 'Workflow', 'Action', 'Performed by full name', 'Performed by role',
      'Reviewed by full name', 'Reviewed by role', 'Officer / custodian', 'Action date/time',
      'Resulting status', 'Before action', 'After action', 'Action details', 'Remarks',
      'Related module', 'Related record type', 'Related record ID',
    ];
    const rows = visibleLogs.map((log) => [
      value(log.firearm_serial),
      workflowLabel(log),
      actionLabel(log),
      value(log.performed_by_full_name || log.performed_by_username),
      value(log.performed_by_role_display || log.performed_by_role_name, 'Unspecified Role'),
      value(log.reviewed_by_full_name || log.reviewed_by_username, 'Not reviewed'),
      value(log.reviewed_by_role_display || log.reviewed_by_role_name, log.reviewed_by ? 'Unspecified Role' : 'Not reviewed'),
      officerLabel(log),
      formatDateTime(log.created_at || log.timestamp),
      resultingStatus(log),
      readableBlock(log.previous_state, 'No before-action data recorded.'),
      readableBlock(log.new_state, 'No after-action data recorded.'),
      readableBlock(log.transaction_data, 'No action details recorded.'),
      value(log.remarks),
      humanizeKnownValue(log.source_app),
      humanizeKnownValue(log.source_model),
      value(log.source_record_id),
    ]);
    const csv = [header, ...rows].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'chain-of-custody.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Chain of Custody</h1><p>Immutable firearm custody movements.</p></div><button className="btn btn-secondary" type="button" onClick={exportCsv}>Export custody history</button></div>
      <FRSNote>FRS 3.3: assigned officer, issue/return dates, current custody/status. Historical records are read-only and cannot be created, edited, or deleted here.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Custody History"
        description="Read-only canonical custody movement records."
        meta={loading ? 'Loading' : `${visibleLogs.length} of ${logs.length}`}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="Search serial, officer, workflow, action"
            onSearchChange={setSearch}
            filters={(
              <>
                <select aria-label="Workflow filter" value={workflowFilter} onChange={(event) => setWorkflowFilter(event.target.value)}>
                  <option value="">All workflows</option>
                  {CHAIN_WORKFLOW_OPTIONS.map((workflow) => <option key={workflow.value} value={workflow.value}>{workflow.label}</option>)}
                </select>
                <select aria-label="Action filter" value={actionFilter} onChange={(event) => setActionFilter(event.target.value)}>
                  <option value="">All actions</option>
                  {CHAIN_ACTION_OPTIONS.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
                </select>
                <select aria-label="Status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">All statuses</option>
                  {statusOptions.map((status) => <option key={`${status.value}-${status.label}`} value={status.label}>{status.label}</option>)}
                </select>
                <input aria-label="Date filter" type="date" max={today()} value={dateFilter} onChange={(event) => setDateFilter(event.target.value)} />
              </>
            )}
            resultCount={loading ? undefined : `${visibleLogs.length} result${visibleLogs.length === 1 ? '' : 's'}`}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Firearm</th><th>Workflow</th><th>Action</th><th>Performed By</th><th>Officer / Custodian</th><th>Action Date / Time</th><th>Resulting Status</th><th>Action / Details</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}><div className="loading-state"><span className="loading-mark" />Loading custody history...</div></td></tr>
            ) : visibleLogs.length === 0 ? (
              <tr><td colSpan={8}><EmptyTableState title="No custody records" description="No custody logs were returned." /></td></tr>
            ) : visibleLogs.map((log) => (
              <tr key={log.id}>
                <td>{value(log.firearm_serial)}</td>
                <td>{workflowLabel(log)}</td>
                <td><TypeBadge type={log.action_type || ''} label={actionLabel(log)} tone={badgeTone(log.action_type)} /></td>
                <td>{actorLabel(log, 'performed_by')}</td>
                <td>{officerLabel(log)}</td>
                <td>{formatDateTime(log.created_at || log.timestamp)}</td>
                <td><StatusBadge status={resultingStatus(log)} label={titleText(resultingStatus(log))} /></td>
                <td><button className="btn btn-secondary" type="button" onClick={() => setSelectedLog(log)}>View Details</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>
      {selectedLog && (
        <DetailDrawer
          open={Boolean(selectedLog)}
          title="Custody Log Details"
          subtitle={value(selectedLog.firearm_serial)}
          meta={<StatusBadge status={resultingStatus(selectedLog)} label={titleText(resultingStatus(selectedLog))} />}
          onClose={() => setSelectedLog(null)}
          closeLabel="Close custody log details"
        >
          <div className="detail-grid custody-detail-grid">
            <div className="full-width custody-summary"><span>Summary</span><p>{custodySummary(selectedLog)}</p></div>
            <div><span>Firearm serial</span><strong>{value(selectedLog.firearm_serial)}</strong></div>
            <div><span>Officer / custodian</span><strong>{officerLabel(selectedLog)}</strong></div>
            <div><span>Workflow</span><strong>{workflowLabel(selectedLog)}</strong></div>
            <div><span>Action</span><strong>{actionLabel(selectedLog)}</strong></div>
            <div><span>Performed by</span><strong>{actorLabel(selectedLog, 'performed_by')}</strong></div>
            <div><span>Reviewed by</span><strong>{actorLabel(selectedLog, 'reviewed_by')}</strong></div>
            <div><span>Action Date / Time</span><strong>{formatDateTime(selectedLog.created_at || selectedLog.timestamp)}</strong></div>
            <div><span>Resulting Status</span><strong>{titleText(resultingStatus(selectedLog))}</strong></div>
            <div className="full-width"><span>Remarks</span><p>{value(selectedLog.remarks, 'No remarks recorded')}</p></div>
            <div className="full-width custody-sections">
              <KeyValueSection title="Before Action" empty="No before-action data recorded." data={selectedLog.previous_state} />
              <KeyValueSection title="After Action" empty="No after-action data recorded." data={selectedLog.new_state} />
              <KeyValueSection title="Action Details" empty="No action details recorded." data={selectedLog.transaction_data} />
            </div>
            <div className="full-width custody-readable-section">
              <h3>Record Metadata</h3>
              <dl>
                <div><dt>Related Module</dt><dd>{humanizeKnownValue(selectedLog.source_app)}</dd></div>
                <div><dt>Related Record Type</dt><dd>{humanizeKnownValue(selectedLog.source_model)}</dd></div>
                <div><dt>Related Record ID</dt><dd>{value(selectedLog.source_record_id)}</dd></div>
              </dl>
            </div>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
};

export const IncidentManagement: React.FC = () => {
  // FRS 3.4: Logistics acknowledges Accountable-submitted incident/loss reports.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canAcknowledge = has('firearm.incident.status.update');
  const [incidents, setIncidents] = React.useState<any[]>([]);
  const [selectedIncident, setSelectedIncident] = React.useState<any | null>(null);
  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [dateFilter, setDateFilter] = React.useState('');
  const [incidentToClose, setIncidentToClose] = React.useState<any | null>(null);
  const [closeRemarks, setCloseRemarks] = React.useState('');
  const [closeRemarksError, setCloseRemarksError] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const { options: incidentTypeOptions } = useReferenceOptions('incident_classifications');
  const { options: incidentStatusOptions } = useReferenceOptions('incident_statuses');

  const load = React.useCallback(async () => {
    setIncidents(await inventoryService.getFirearmIncidents(adminFilter));
  }, [adminFilter]);

  React.useEffect(() => { void load().catch(() => setError('Incident records are temporarily unavailable.')); }, [load]);

  const acknowledge = async (id: number) => {
    try {
      await inventoryService.acknowledgeFirearmIncident(id);
      setMessage('Incident report acknowledged.');
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Incident acknowledgment could not be saved.'));
    }
  };

  const openCloseIncident = (incident: any) => {
    setIncidentToClose(incident);
    setCloseRemarks('');
    setCloseRemarksError('');
  };

  const cancelCloseIncident = () => {
    setIncidentToClose(null);
    setCloseRemarks('');
    setCloseRemarksError('');
  };

  const confirmCloseIncident = async () => {
    if (!incidentToClose) return;
    const remarks = closeRemarks.trim();
    if (!remarks) {
      setCloseRemarksError('Close remarks are required.');
      return;
    }
    try {
      await inventoryService.closeFirearmIncident(incidentToClose.id, remarks);
      cancelCloseIncident();
      setMessage('Incident closed.');
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Incident could not be closed.'));
    }
  };

  const visibleIncidents = incidents.filter((incident) => {
    const haystack = `${incident.id || ''} ${incident.firearm_serial || ''} ${incident.officer_name || ''} ${incident.location || ''} ${incident.description || ''}`.toLowerCase();
    const matchesSearch = !search || haystack.includes(search.toLowerCase());
    const matchesType = !typeFilter || incident.incident_type === typeFilter;
    const matchesStatus = !statusFilter || (incident.status_group || incident.status) === statusFilter;
    const matchesDate = !dateFilter || incident.incident_date === dateFilter;
    return matchesSearch && matchesType && matchesStatus && matchesDate;
  });

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div><h1>Incident Reports</h1><p>Acknowledge Accountable-submitted incident reports and view incident history.</p></div>
      </div>
      <FRSNote>FRS 3.4: Logistics acknowledges submitted reports. Backend applies firearm status changes only after acknowledgment.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}

      <RecordsTableCard
        title="Incident Records"
        description="Acknowledge reported firearm incidents and monitor logistics status."
        meta={`${visibleIncidents.length} of ${incidents.length}`}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="Search incident, serial, officer, location"
            onSearchChange={setSearch}
            filters={(
              <>
                <select aria-label="Incident type filter" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                  <option value="">All types</option>
                  {incidentTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select aria-label="Incident status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">All statuses</option>
                  {incidentStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <input aria-label="Incident date filter" type="date" max={today()} value={dateFilter} onChange={(event) => setDateFilter(event.target.value)} />
              </>
            )}
            resultCount={`${visibleIncidents.length} result${visibleIncidents.length === 1 ? '' : 's'}`}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Incident No.</th><th>Firearm Serial</th><th>Incident Type</th><th>Incident Date</th><th>Location</th><th>Incident Status</th><th>Firearm Status</th><th>Action</th></tr></thead>
          <tbody>
            {visibleIncidents.length === 0 ? (
              <tr><td colSpan={8}><EmptyTableState title="No incident records" description="No incident reports matched the current filters." /></td></tr>
            ) : visibleIncidents.map((incident) => (
              <tr key={incident.id}>
                <td>INC-{incident.id}</td>
                <td>{value(incident.firearm_serial)}</td>
                <td><TypeBadge type={incident.incident_type || ''} label={titleText(incident.incident_type)} tone={badgeTone(incident.incident_type)} /></td>
                <td>{formatDate(incident.incident_date)}</td>
                <td>{value(incident.location)}</td>
                <td><StatusBadge status={incident.status || ''} label={incidentStatusLabel(incident.status)} /></td>
                <td><StatusBadge status={incident.firearm_status || ''} label={titleText(incident.firearm_status)} /></td>
                <td>
                  <div className="table-actions">
                    <button className="btn btn-secondary" type="button" onClick={() => setSelectedIncident(incident)}>View</button>
                    {canAcknowledge && incident.status === 'reported' ? (
                      <button className="btn btn-primary" type="button" onClick={() => acknowledge(incident.id)}>Acknowledge Report</button>
                    ) : canAcknowledge && incident.status === 'under_investigation' ? (
                      <button className="btn btn-primary" type="button" onClick={() => openCloseIncident(incident)}>Close Incident</button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>

      {selectedIncident && (
        <DetailDrawer
          open={Boolean(selectedIncident)}
          title={`Incident INC-${selectedIncident.id}`}
          subtitle={value(selectedIncident.firearm_serial)}
          meta={<StatusBadge status={selectedIncident.status || ''} label={incidentStatusLabel(selectedIncident.status)} />}
          onClose={() => setSelectedIncident(null)}
          closeLabel="Close incident details"
        >
          <div className="detail-grid">
            <div><span>Firearm</span><strong>{value(selectedIncident.firearm_serial)}</strong></div>
            <div><span>Type</span><strong>{statusText(selectedIncident.incident_type)}</strong></div>
            <div><span>Status</span><strong>{incidentStatusLabel(selectedIncident.status)}</strong></div>
            <div><span>Firearm status</span><strong>{statusText(selectedIncident.firearm_status)}</strong></div>
            <div><span>Date</span><strong>{formatDate(selectedIncident.incident_date)}</strong></div>
            <div><span>Location</span><strong>{value(selectedIncident.location)}</strong></div>
            <div className="full-width"><span>Description</span><p>{value(selectedIncident.description)}</p></div>
          </div>
        </DetailDrawer>
      )}

      <FormModal
        open={Boolean(incidentToClose)}
        title="Close Incident"
        description={incidentToClose ? `INC-${incidentToClose.id} - ${value(incidentToClose.firearm_serial)}` : undefined}
        onClose={cancelCloseIncident}
        closeLabel="Cancel incident closure"
        showExpandToggle={false}
        footer={(
          <>
            <button className="btn btn-secondary" type="button" onClick={cancelCloseIncident}>Cancel</button>
            <button className="btn btn-primary" type="button" onClick={confirmCloseIncident}>Confirm Close</button>
          </>
        )}
      >
        <div className="form-grid">
          <label className="full-width">
            Remarks
            <textarea
              aria-label="Close incident remarks"
              value={closeRemarks}
              onChange={(event) => {
                setCloseRemarks(event.target.value);
                if (closeRemarksError) setCloseRemarksError('');
              }}
              rows={4}
              required
            />
          </label>
          {closeRemarksError ? <div className="field-error full-width" role="alert">{closeRemarksError}</div> : null}
        </div>
      </FormModal>
    </div>
  );
};

export const IssuanceReview: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // FRS 4.2-4.3: Logistics review of issuance/ARE records without creating new issuance workflows.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canApprove = has('firearm.issuance.approve');
  const canReject = has('firearm.issuance.reject');
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [registryFirearms, setRegistryFirearms] = React.useState<Firearm[]>([]);
  const [multiCases, setMultiCases] = React.useState<MultiIssuanceCase[]>([]);
  const [selectedCase, setSelectedCase] = React.useState<MultiIssuanceCase | null>(null);
  const { options: statusOptions } = useReferenceOptions('issuance_statuses');
  const [resolutionType, setResolutionType] = React.useState<'retain_existing' | 'refer_custody_correction' | 'false_positive'>('retain_existing');
  const [resolutionNotes, setResolutionNotes] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [recordGroup, setRecordGroup] = React.useState<'open' | 'history' | 'all'>('open');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [selectedMultiIssuance, setSelectedMultiIssuance] = React.useState<Issuance | null>(null);
  const [selectedDetailIssuance, setSelectedDetailIssuance] = React.useState<Issuance | null>(null);
  const [selectedAuthorizationReview, setSelectedAuthorizationReview] = React.useState<Issuance | null>(null);
  const [authorizationDecision, setAuthorizationDecision] = React.useState<'accept' | 'reject'>('accept');
  const [authorizationReviewRemarks, setAuthorizationReviewRemarks] = React.useState('');

  const load = React.useCallback(async () => {
    const [nextIssuances, nextCases, nextFirearms] = await Promise.all([
      issuanceService.getIssuances(adminFilter), issuanceService.getMultiIssuanceCases(adminFilter), inventoryService.getFirearms(adminFilter),
    ]);
    setIssuances(nextIssuances);
    setMultiCases(nextCases);
    setRegistryFirearms(nextFirearms || []);
  }, [adminFilter]);

  React.useEffect(() => { void load().catch(() => setError('Issuance records are temporarily unavailable.')); }, [load]);

  const closeAuthorizationReview = () => {
    setSelectedAuthorizationReview(null);
    setAuthorizationDecision('accept');
    setAuthorizationReviewRemarks('');
  };

  const openAuthorizationReview = (issuance: Issuance, decision: 'accept' | 'reject' = 'accept') => {
    setSelectedAuthorizationReview(issuance);
    setAuthorizationDecision(decision);
    setAuthorizationReviewRemarks('');
  };

  const approve = async (issuance: Issuance) => {
    if (issuance.status === 'authorization_review') {
      openAuthorizationReview(issuance, 'accept');
      return;
    }
    try {
      await issuanceService.approveIssuance(issuance.id);
      setMessage('Issuance approved.');
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Issuance could not be approved.'));
    }
  };

  const viewAuthorizationDocument = async (issuance: Issuance) => {
    try {
      const blob = await issuanceService.getAuthorizationDocument(issuance.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      setError(errorText(err, 'Authorization document could not be opened.'));
    }
  };

  const reject = (issuance: Issuance) => {
    openAuthorizationReview(issuance, 'reject');
  };

  const submitAuthorizationDecision = async (event: React.FormEvent) => {
    event.preventDefault();
    const issuance = selectedAuthorizationReview;
    const remarks = authorizationReviewRemarks.trim();
    if (!issuance || !remarks) {
      setError('Review remarks are required.');
      return;
    }
    try {
      if (authorizationDecision === 'accept') {
        await issuanceService.approveIssuance(issuance.id, { remarks });
        setMessage('Authorization accepted and issuance approved.');
      } else {
        await issuanceService.rejectIssuance(issuance.id, { rejection_reason: remarks });
        setMessage('Issuance rejected.');
      }
      closeAuthorizationReview();
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Authorization review could not be completed.'));
    }
  };

  const resolveCase = async () => {
    if (!selectedCase || !resolutionNotes.trim()) {
      setError('Resolution remarks are required.'); return;
    }
    try {
      await issuanceService.resolveMultiIssuanceCase(selectedCase.id, {
        resolution_type: resolutionType, resolution_notes: resolutionNotes.trim(),
      });
      setSelectedCase(null); setResolutionNotes(''); setMessage('Multi-issuance case updated.'); await load();
    } catch (err: any) { setError(errorText(err, 'Multi-issuance case could not be resolved.')); }
  };

  const activeIssuances = issuances.filter(isVisibleActiveIssuance);
  const historyStatuses = new Set(['returned', 'revoked', 'incident_closed', 'rejected', 'cancelled']);
  const reviewIssuances = issuances.filter((issuance) => (
    recordGroup === 'all'
    || (recordGroup === 'history' && historyStatuses.has(issuance.status))
    || (recordGroup === 'open' && (isVisibleActiveIssuance(issuance) || isResolvedMultiIssuance(issuance)))
  ));
  const visibleIssuances = reviewIssuances.filter((issuance) => {
    const department = issuanceDepartment(issuance);
    const status = value(issuance.status_display || issuance.status, '');
    return textMatches([issuanceFirearm(issuance), issuance.firearm_model_display, issuanceOfficerText(issuance), department, status], search)
      && (!statusFilter || (issuance.status_group || issuance.status) === statusFilter);
  });

  const firearmForIssuance = (issuance: Issuance): Firearm | undefined => registryFirearms.find(
    (item) => item.id === issuance.firearm || item.serial_number === issuance.firearm_serial
  );

  return (
    <div className={`admin-dashboard inspector-workspace${embedded ? ' logistics-embedded-records' : ''}`}>
      {!embedded && <div className="page-heading"><div><h1>Issuance / ARE Review</h1><p>Review issuance, firearm, officer, validation, and ARE status.</p></div></div>}
      <FRSNote>FRS 4.2-4.3: review only. Target firearm availability and officer eligibility are enforced by backend/RBAC.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Acknowledge Issuance"
        description="Review issuance status and approve or reject submitted records where permitted."
        meta={`${visibleIssuances.length} result${visibleIssuances.length === 1 ? '' : 's'} • ${activeIssuances.length} active`}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="Search firearm, officer, department"
            onSearchChange={setSearch}
            filters={(
              <>
                <select
                  aria-label="Issuance record group"
                  value={recordGroup}
                  onChange={(event) => setRecordGroup(event.target.value as typeof recordGroup)}
                >
                  <option value="open">Open Issuances</option>
                  <option value="history">Issuance History</option>
                  <option value="all">All Records</option>
                </select>
                <select aria-label="Issuance status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">All statuses</option>
                  {statusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
              </>
            )}
            resultCount={`${visibleIssuances.length} result${visibleIssuances.length === 1 ? '' : 's'}`}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Issuance Reference</th><th>Firearm</th><th>Officer</th><th>LTOPF Status</th><th>Department</th><th>Issued Date</th><th>Status</th><th>Review Type</th><th>Active Assignment</th><th>Firearm Image</th><th>Action</th></tr></thead>
          <tbody>
            {visibleIssuances.length === 0 ? (
              <tr><td colSpan={11}><EmptyTableState title="No issuance records" description="No issuance records matched the current filters." /></td></tr>
            ) : visibleIssuances.map((issuance) => {
              const authorizationReview = issuance.status === 'authorization_review';
              const pending = issuance.status === 'submitted' || authorizationReview;
              const multiSummary = multiIssuanceSummary(issuance);
              const resolvedMulti = isResolvedMultiIssuance(issuance);
              const unresolvedMulti = Boolean(issuance.multi_issuance?.detected && !resolvedMulti);
              const multiDetected = hasMultiIssuance(issuance);
              const canApproveIssuance = !resolvedMulti && pending && canApprove && issuance.can_approve !== false && !issuance.approval_blocked;
              const canRejectIssuance = !resolvedMulti && canReject && pending && !unresolvedMulti;
              const showActionButtons = pending || issuance.status === 'multi_issuance' || multiDetected;
              return (
                <tr key={issuance.id}>
                  <td>{`ISS-${issuance.id}`}</td>
                  <td>{issuanceFirearm(issuance)}</td>
                  <td>{issuanceOfficerText(issuance)}</td>
                  <td>
                    <StatusBadge
                      status={issuance.officer_ltopf_status || 'valid'}
                      label={issuance.officer_ltopf_expires_today ? 'Expires Today' : titleText(issuance.officer_ltopf_status || 'valid')}
                      tone={issuance.officer_ltopf_status === 'expired' ? 'danger' : issuance.officer_ltopf_status === 'valid' ? 'success' : 'warning'}
                    />
                    <div className="form-helper-muted">{formatDate(issuance.officer_ltopf_expiration_date)}</div>
                  </td>
                  <td>{value(issuanceDepartment(issuance))}</td>
                  <td>{formatDate(issuance.issued_at || issuance.requested_at)}</td>
                  <td>
                    <StatusBadge status={issuance.status} label={issuance.status_display || titleText(issuance.status)} />
                    {multiSummary ? <div className="form-helper-muted">{multiSummary}</div> : null}
                  </td>
                  <td>{issuance.review_type && issuance.review_type !== 'none' ? (issuance.review_type_display || titleText(issuance.review_type)) : 'None'}</td>
                  <td>
                    <StatusBadge
                      status={issuance.has_active_assignment ? 'active' : 'inactive'}
                      label={issuance.active_assignment_label || (issuance.has_active_assignment ? 'Active' : 'Inactive')}
                      tone={issuance.has_active_assignment ? 'success' : 'neutral'}
                    />
                  </td>
                  <td><StatusBadge status={firearmForIssuance(issuance)?.primary_image ? 'available' : 'missing'} label={firearmForIssuance(issuance)?.primary_image ? 'Available' : 'No Image'} tone={firearmForIssuance(issuance)?.primary_image ? 'success' : 'warning'} /></td>
                  <td>{showActionButtons ? (
                    <div className="table-actions">
                      {authorizationReview && issuance.authorization_review?.document_available ? <button className="btn btn-secondary" type="button" onClick={() => void viewAuthorizationDocument(issuance)}>View Authorization</button> : null}
                      {canApproveIssuance && <button className="btn btn-primary" type="button" onClick={() => void approve(issuance)}>{authorizationReview ? 'Review Authorization' : 'Approve'}</button>}
                      {multiDetected && <button className="btn btn-secondary" type="button" onClick={() => setSelectedMultiIssuance(issuance)}>View Details</button>}
                      {canRejectIssuance && <button className="btn btn-secondary" type="button" onClick={() => reject(issuance)}>Reject</button>}
                      {!multiDetected ? <button className="btn btn-secondary" type="button" onClick={() => setSelectedDetailIssuance(issuance)}>View Firearm Details</button> : null}
                      {!canApproveIssuance && !multiDetected && !canRejectIssuance ? <span className="records-table-muted">Read-only</span> : null}
                    </div>
                  ) : (
                    <button className="btn btn-secondary" type="button" onClick={() => setSelectedDetailIssuance(issuance)}>View Firearm Details</button>
                  )}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RecordsTableCard>
      <RecordsTableCard title="Multi-Issuance Cases" description="Grouped blocked attempts; authoritative custody remains unchanged." meta={`${multiCases.length} case${multiCases.length === 1 ? '' : 's'}`}>
        <table className="records-table"><thead><tr><th>Case</th><th>Firearm</th><th>Current Custodian</th><th>Attempts</th><th>Opened</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>{multiCases.length ? multiCases.map((item) => <tr key={item.id}><td>{item.id}</td><td>{item.firearm_serial}</td><td>{item.current_custodian || 'Unverified'}</td><td>{item.attempts.length}</td><td>{formatDate(item.opened_at)}</td><td><StatusBadge status={item.status} label={titleText(item.resolution_type || item.status)} /></td><td><button className="btn btn-secondary" type="button" onClick={() => { setSelectedCase(item); setResolutionType(item.can_retain_existing && !item.requires_manual_review ? 'retain_existing' : 'refer_custody_correction'); }}>Review case</button></td></tr>) : <tr><td colSpan={7}><EmptyTableState title="No multi-issuance cases" description="No blocked assignment cases matched this scope." /></td></tr>}</tbody>
        </table>
      </RecordsTableCard>
      {selectedCase && <DetailDrawer open title={`Multi-Issuance Case ${selectedCase.id}`} subtitle={selectedCase.firearm_serial} meta={<StatusBadge status={selectedCase.status} label={titleText(selectedCase.status)} />} onClose={() => setSelectedCase(null)}>
        <div className="detail-grid"><div><span>Current custodian</span><strong>{selectedCase.current_custodian || 'Unverified'}</strong></div><div><span>Custody review</span><strong>{selectedCase.requires_manual_review ? 'Manual review required' : 'Authority verified'}</strong></div><div><span>Authoritative issuance</span><strong>{selectedCase.authoritative_issuance || 'Unverified'}</strong></div><div><span>Authoritative ARE</span><strong>{statusText(selectedCase.authoritative_are_status || 'Not available')}</strong></div><div><span>Attempts</span><strong>{selectedCase.attempts.length}</strong></div></div>
        <table className="records-table"><thead><tr><th>Officer</th><th>Attempted by</th><th>Date</th><th>Notes</th><th>Status</th></tr></thead><tbody>{selectedCase.attempts.map((attempt) => <tr key={attempt.id}><td>{attempt.attempted_officer_name}</td><td>{attempt.attempted_by_username}</td><td>{formatDate(attempt.attempted_at)}</td><td>{attempt.notes}</td><td>{titleText(attempt.disposition)}</td></tr>)}</tbody></table>
        {selectedCase.status !== 'resolved' && <div className="inspector-form"><label>Resolution<select value={resolutionType} onChange={(e) => setResolutionType(e.target.value as typeof resolutionType)}><option value="retain_existing" disabled={!selectedCase.can_retain_existing || selectedCase.requires_manual_review}>Retain Existing Issuance</option><option value="refer_custody_correction">Refer for Custody Correction</option><option value="false_positive">False Positive</option></select></label><label className="full-width">Required resolution remarks<textarea rows={4} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} required /></label><div className="form-actions full-width"><button className="btn btn-primary" type="button" disabled={resolutionType === 'retain_existing' && (!selectedCase.can_retain_existing || selectedCase.requires_manual_review)} onClick={() => void resolveCase()}>{resolutionType === 'retain_existing' ? 'Resolve - Retain Existing' : resolutionType === 'false_positive' ? 'Resolve - False Positive' : 'Refer for Correction'}</button></div></div>}
      </DetailDrawer>}
      {selectedAuthorizationReview && (
        <FormModal
          open
          title={selectedAuthorizationReview.status === 'authorization_review' ? 'Review Additional-Handgun Authorization' : 'Reject Issuance'}
          description={`ISS-${selectedAuthorizationReview.id}`}
          onClose={closeAuthorizationReview}
        >
          <form className="inspector-form" onSubmit={submitAuthorizationDecision}>
            {selectedAuthorizationReview.status === 'authorization_review' ? (
              <label>Decision<select value={authorizationDecision} onChange={(event) => setAuthorizationDecision(event.target.value as 'accept' | 'reject')}><option value="accept">Accept authority and approve</option><option value="reject">Reject authorization</option></select></label>
            ) : (
              <div className="form-helper-muted full-width">Rejecting this issuance requires reviewer-entered remarks.</div>
            )}
            <label className="full-width">Required review remarks<textarea rows={4} required value={authorizationReviewRemarks} onChange={(event) => setAuthorizationReviewRemarks(event.target.value)} /></label>
            <div className="form-actions full-width">
              <button className="btn btn-primary" type="submit">Submit decision</button>
              <button className="btn btn-secondary" type="button" onClick={closeAuthorizationReview}>Cancel</button>
            </div>
          </form>
        </FormModal>
      )}
      {selectedMultiIssuance && (
        <DetailDrawer
          open={Boolean(selectedMultiIssuance)}
          title="Conflict — Multi-Issuance Details"
          subtitle={issuanceFirearm(selectedMultiIssuance)}
          meta={<StatusBadge status={selectedMultiIssuance.status} label={selectedMultiIssuance.status_display || titleText(selectedMultiIssuance.status)} />}
          onClose={() => setSelectedMultiIssuance(null)}
          closeLabel="Close Multi-Issuance details"
        >
          {selectedMultiIssuance.status === 'rejected' && selectedMultiIssuance.multi_issuance?.resolved ? (
            <div className="full-width custody-summary"><span>Summary</span><p>This request was rejected after Logistics resolved a Multi-Issuance conflict.</p></div>
          ) : null}
          <div className="detail-grid">
            {multiIssuanceDetailRows(selectedMultiIssuance).map(([label, detail]) => (
              <div key={label}><span>{label}</span><strong>{detail}</strong></div>
            ))}
          </div>
        </DetailDrawer>
      )}
      {selectedDetailIssuance && (
        <FirearmIssuanceDetailsDrawer
          issuance={selectedDetailIssuance}
          firearm={firearmForIssuance(selectedDetailIssuance)}
          onClose={() => setSelectedDetailIssuance(null)}
        />
      )}
    </div>
  );
};

export const ARECountersigning: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // FRS 4.3: Logistics review of Accountable ARE upload records.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const [ares, setAres] = React.useState<ARE[]>([]);
  const [search, setSearch] = React.useState('');
  const [uploadFilter, setUploadFilter] = React.useState('');
  const [expirationFilter, setExpirationFilter] = React.useState('');
  const [error, setError] = React.useState('');
  const [documentPreview, setDocumentPreview] = React.useState<AREDocumentPreviewState | null>(null);

  const load = React.useCallback(async () => {
    setAres(await issuanceService.getAres(adminFilter));
  }, [adminFilter]);
  React.useEffect(() => { void load().catch(() => setError('ARE records are temporarily unavailable.')); }, [load]);

  const closeDocumentPreview = React.useCallback(() => {
    setDocumentPreview((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
      return null;
    });
  }, []);

  React.useEffect(() => closeDocumentPreview, [closeDocumentPreview]);

  const openDocumentPreview = async (are: ARE) => {
    const filename = areDocumentName(are) || `ARE-${are.id} document`;
    setDocumentPreview((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
      return { areId: are.id, filename, loading: true };
    });
    try {
      const blob = await issuanceService.getAreDocument(are.id);
      const objectUrl = URL.createObjectURL(blob);
      setDocumentPreview((current) => {
        if (!current || current.areId !== are.id) {
          URL.revokeObjectURL(objectUrl);
          return current;
        }
        if (current.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return {
          ...current,
          objectUrl,
          contentType: blob.type || are.document_mime_type || undefined,
          loading: false,
          error: undefined,
        };
      });
    } catch (err: any) {
      setDocumentPreview((current) => {
        if (!current || current.areId !== are.id) return current;
        return {
          ...current,
          loading: false,
          error: errorText(err, 'ARE document preview could not be loaded.'),
        };
      });
    }
  };

  const visibleAres = ares.filter((are) =>
    textMatches([`ARE-${are.id}`, are.firearm_serial_number, are.officer_name, are.officer_badge_number, are.officer_unit, areUploadStatusLabel(are), are.document_original_name, are.expiration_date], search)
    && (!uploadFilter || areUploadStatus(are) === uploadFilter)
    && (!expirationFilter || are.expiration_status === expirationFilter)
  );

  return (
    <div className={`admin-dashboard inspector-workspace${embedded ? ' logistics-embedded-records' : ''}`}>
      {!embedded && <div className="page-heading"><div><h1>ARE Upload Records</h1><p>Review Accountable ARE uploads and expiration dates.</p></div></div>}
      <FRSNote>FRS 4.3: Logistics reviews ARE upload records after issuance approval.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="ARE Upload Records"
        description="Review ARE upload state, expiration dates, and uploaded documents."
        meta={`${visibleAres.length} of ${ares.length}`}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="Search ARE, firearm, officer"
            onSearchChange={setSearch}
            filters={(
              <>
                <select aria-label="Upload status filter" value={uploadFilter} onChange={(event) => setUploadFilter(event.target.value)}>
                  <option value="">All ARE Uploads</option>
                  <option value="pending_accountable_are">Pending Accountable ARE</option>
                  <option value="are_uploaded">ARE Uploaded</option>
                  <option value="missing_expiration">Missing Expiration</option>
                </select>
                <select aria-label="Expiration status filter" value={expirationFilter} onChange={(event) => setExpirationFilter(event.target.value)}>
                  <option value="">All Expiration Statuses</option>
                  <option value="valid">Valid</option>
                  <option value="expiring_soon">Expiring Soon</option>
                  <option value="expired">Expired</option>
                  <option value="missing_expiration">Missing Expiration</option>
                </select>
              </>
            )}
            resultCount={`${visibleAres.length} result${visibleAres.length === 1 ? '' : 's'}`}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>ARE No.</th><th>Firearm</th><th>Officer</th><th>Upload Status</th><th>Expiration Status</th><th>ARE Expiration</th><th>Days Remaining</th><th>Uploaded Document</th><th>Action</th></tr></thead>
          <tbody>
            {visibleAres.length === 0 ? (
              <tr><td colSpan={9}><EmptyTableState title="No ARE records" description="No ARE records matched the current filters." /></td></tr>
            ) : visibleAres.map((are) => (
                <tr key={are.id}>
                  <td>ARE-{are.id}</td>
                  <td>{value(are.firearm_serial_number)}</td>
                  <td>{value(are.officer_name || are.officer_badge_number)}</td>
                  <td><StatusBadge status={areUploadStatus(are)} label={areUploadStatusLabel(are)} tone={areUploadTone(are)} /></td>
                  <td><StatusBadge status={are.expiration_status || 'missing_expiration'} label={areExpirationLabel(are)} tone={areExpirationTone(are)} /></td>
                  <td>{formatDate(are.expiration_date)}</td>
                  <td>{are.expiration_days_remaining == null ? '-' : are.expiration_days_remaining}</td>
                  <td>
                    {areDocumentName(are) ? (
                      <button className="document-preview-link" type="button" title={areDocumentName(are)} onClick={() => openDocumentPreview(are)}>
                        {areDocumentPreviewLabel(are)}
                      </button>
                    ) : (
                      <StatusBadge status="pending_accountable_are" label="No document" tone="warning" />
                    )}
                  </td>
                  <td>
                    <span className="records-table-muted">{isAreUploaded(are) ? 'Ready for review' : 'Pending upload'}</span>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>
      <DocumentPreviewModal
        open={Boolean(documentPreview)}
        title={`ARE-${documentPreview?.areId || ''} Document Preview`}
        filename={documentPreview?.filename}
        objectUrl={documentPreview?.objectUrl}
        contentType={documentPreview?.contentType}
        loading={documentPreview?.loading}
        error={documentPreview?.error}
        onClose={closeDocumentPreview}
      />
    </div>
  );
};

export const IssuanceAndARE: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<'issuances' | 'ares'>('issuances');
  return (
    <div className="admin-dashboard inspector-workspace logistics-wide-workspace">
      <div className="page-heading">
        <div><h1>Issuance and ARE</h1><p>Acknowledge issuance requests and review ARE uploads.</p></div>
      </div>
      <div className="records-segmented-tabs" role="tablist" aria-label="Issuance and ARE sections">
        <button type="button" role="tab" aria-selected={activeTab === 'issuances'} className={activeTab === 'issuances' ? 'active' : ''} onClick={() => setActiveTab('issuances')}>Acknowledge Issuance</button>
        <button type="button" role="tab" aria-selected={activeTab === 'ares'} className={activeTab === 'ares' ? 'active' : ''} onClick={() => setActiveTab('ares')}>ARE Upload Records</button>
      </div>
      {activeTab === 'issuances' ? <IssuanceReview embedded /> : <ARECountersigning embedded />}
    </div>
  );
};

export const FirearmReturns: React.FC = () => {
  // FRS 4.4: Logistics return oversight and confirmation.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canConfirm = has('firearm.return.confirm.logistics');
  const [activeTab, setActiveTab] = React.useState<'issuance' | 'records' | 'repairs'>('issuance');
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [records, setRecords] = React.useState<ReturnRecord[]>([]);
  const [repairs, setRepairs] = React.useState<FirearmRepair[]>([]);
  const [issuanceSearch, setIssuanceSearch] = React.useState('');
  const [issuanceStatus, setIssuanceStatus] = React.useState('');
  const [recordSearch, setRecordSearch] = React.useState('');
  const [recordStatus, setRecordStatus] = React.useState('');
  const [recordDate, setRecordDate] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [selectedReturnEvidence, setSelectedReturnEvidence] = React.useState<ReturnRecord | null>(null);
  const [selectedRepair, setSelectedRepair] = React.useState<FirearmRepair | null>(null);
  const [completingRepair, setCompletingRepair] = React.useState<FirearmRepair | null>(null);
  const { options: issuanceStatusOptions } = useReferenceOptions('issuance_statuses');
  const { options: returnStatusOptions } = useReferenceOptions('return_statuses');

  const load = React.useCallback(async () => {
    const [nextIssuances, nextRecords] = await Promise.all([
      issuanceService.getIssuances(adminFilter),
      inventoryService.getReturnRecords(adminFilter),
    ]);
    setIssuances(nextIssuances);
    setRecords(nextRecords);
  }, [adminFilter]);

  const loadRepairs = React.useCallback(async () => {
    setRepairs(await repairService.listRepairs(adminFilter));
  }, [adminFilter]);

  React.useEffect(() => { void load().catch(() => setError('Return oversight records are temporarily unavailable.')); }, [load]);
  React.useEffect(() => {
    if (activeTab === 'repairs') {
      void loadRepairs().catch(() => setError('Repair records are temporarily unavailable.'));
    }
  }, [activeTab, loadRepairs]);

  const confirm = async (id: number) => {
    try {
      await issuanceService.confirmReturn(id);
      setMessage('Return confirmed.');
      setSelectedReturnEvidence(null);
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Return could not be confirmed.'));
    }
  };

  const currentIssuances = issuances.filter(isVisibleReturnIssuance);
  const visibleIssuances = currentIssuances.filter((issuance) =>
    textMatches([issuanceFirearm(issuance), issuanceOfficerText(issuance), issuance.status, issuance.status_display, issuance.issued_at, issuance.requested_at, issuanceDepartment(issuance)], issuanceSearch)
    && (!issuanceStatus || (issuance.status_group || issuance.status) === issuanceStatus)
  );
  const visibleRecords = records.filter((record) =>
    textMatches([record.firearm_serial, record.officer_name, record.officer_badge, record.status, record.action_type, record.reason, record.firearm_condition], recordSearch)
    && (!recordStatus || (record.status_group || record.status) === recordStatus)
    && (!recordDate || value(record.returned_at || record.created_at, '').startsWith(recordDate))
  );

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Firearm Returns</h1><p>Pending returns, Logistics confirmation context, and return history.</p></div></div>
      <FRSNote>FRS 4.4: existing backend-supported return review and history are exposed. Unsupported confirmation actions are hidden rather than mocked.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <div className="records-segmented-tabs" role="tablist" aria-label="Firearm return sections">
        <button type="button" role="tab" aria-selected={activeTab === 'issuance'} className={activeTab === 'issuance' ? 'active' : ''} onClick={() => setActiveTab('issuance')}>Issuance Status</button>
        <button type="button" role="tab" aria-selected={activeTab === 'records'} className={activeTab === 'records' ? 'active' : ''} onClick={() => setActiveTab('records')}>Return Records</button>
        <button type="button" role="tab" aria-selected={activeTab === 'repairs'} className={activeTab === 'repairs' ? 'active' : ''} onClick={() => setActiveTab('repairs')}>Repair Records</button>
      </div>
      {activeTab === 'issuance' ? (
        <RecordsTableCard
          title="Issuance Status"
          description="Read-only issuance context for return oversight."
          meta={`${visibleIssuances.length} of ${currentIssuances.length} current`}
          toolbar={(
            <CompactTableToolbar
              searchValue={issuanceSearch}
              searchPlaceholder="Search firearm, officer, status"
              onSearchChange={setIssuanceSearch}
              filters={(
                <>
                  <select aria-label="Issuance status filter" value={issuanceStatus} onChange={(event) => setIssuanceStatus(event.target.value)}>
                    <option value="">All statuses</option>
                    {issuanceStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                  </select>
                </>
              )}
              resultCount={`${visibleIssuances.length} result${visibleIssuances.length === 1 ? '' : 's'}`}
            />
          )}
        >
          <table className="records-table">
            <thead><tr><th>Firearm</th><th>Officer</th><th>Issuance Status</th><th>Issued Date</th><th>Returned Date</th><th>Action</th></tr></thead>
            <tbody>
              {visibleIssuances.length === 0 ? (
                <tr><td colSpan={6}><EmptyTableState title="No issuance records" description="No active or returned issuance records matched the current filters." /></td></tr>
              ) : visibleIssuances.map((issuance) => {
                const multiSummary = multiIssuanceSummary(issuance);
                return (
                  <tr key={issuance.id}>
                    <td>{issuanceFirearm(issuance)}</td>
                    <td>{issuanceOfficerText(issuance)}</td>
                    <td>
                      <StatusBadge status={issuance.status} label={titleText(issuance.status_display || issuance.status)} />
                      {multiSummary ? <div className="form-helper-muted">{multiSummary}</div> : null}
                    </td>
                    <td>{formatDate(issuance.issued_at || issuance.requested_at)}</td>
                    <td>{formatDate(issuance.returned_at)}</td>
                    <td><span>{issuance.approval_blocked ? 'Blocked request is not returnable' : 'Read-only oversight'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </RecordsTableCard>
      ) : activeTab === 'records' ? (
        <RecordsTableCard
          title="Return Records"
          description="Submitted return records and logistics confirmation actions."
          meta={`${visibleRecords.length} of ${records.length}`}
          toolbar={(
            <CompactTableToolbar
              searchValue={recordSearch}
              searchPlaceholder="Search firearm, officer, return"
              onSearchChange={setRecordSearch}
              filters={(
                <>
                  <select aria-label="Return status filter" value={recordStatus} onChange={(event) => setRecordStatus(event.target.value)}>
                    <option value="">All return statuses</option>
                    {returnStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                  </select>
                  <input aria-label="Return date filter" type="date" max={today()} value={recordDate} onChange={(event) => setRecordDate(event.target.value)} />
                </>
              )}
              resultCount={`${visibleRecords.length} result${visibleRecords.length === 1 ? '' : 's'}`}
            />
          )}
        >
          <table className="records-table">
            <thead><tr><th>Firearm</th><th>Officer</th><th>Return Status</th><th>Condition</th><th>Submitted On</th><th>Confirmed On</th><th>Action</th></tr></thead>
            <tbody>
              {visibleRecords.length === 0 ? (
                <tr><td colSpan={7}><EmptyTableState title="No return records" description="No return history records matched the current filters." /></td></tr>
              ) : visibleRecords.map((record) => {
              const pending = record.status === 'submitted' || record.status === 'pending';
              return (
                <tr key={record.id}>
                  <td>{value(record.firearm_serial)}</td>
                  <td>{value(record.officer_name || record.officer_badge)}</td>
                  <td><StatusBadge status={record.status || ''} label={titleText(record.status)} /></td>
                  <td>{titleText(record.firearm_condition || record.action_type || record.reason)}</td>
                  <td>{formatDate(record.returned_at || record.created_at)}</td>
                  <td>{formatDate(record.confirmed_at)}</td>
                  <td>
                    {pending && canConfirm ? (
                      // Phase 10 return workflow is confirm-only. Logistics is
                      // not accepting or rejecting the request; confirmation
                      // means the submitted return has been viewed and triggers
                      // the backend firearm/issuance state change.
                      <button className="btn btn-primary" type="button" onClick={() => setSelectedReturnEvidence(record)}>Review Evidence</button>
                    ) : (
                      <span className="records-table-muted">Read-only</span>
                    )}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </RecordsTableCard>
      ) : (
        <RecordsTableCard
          title="Repair Records"
          description="Controlled firearm repair history and completion actions."
          meta={`${repairs.length} repair${repairs.length === 1 ? '' : 's'}`}
        >
          <table className="records-table">
            <thead><tr><th>Repair</th><th>Firearm</th><th>Received Condition</th><th>Repair Status</th><th>Started</th><th>Completed</th><th>Action</th></tr></thead>
            <tbody>
              {repairs.length ? repairs.map((repair) => (
                <tr key={repair.id}>
                  <td>{`REP-${repair.id}`}</td>
                  <td>{repair.firearm_serial_number}</td>
                  <td>{repair.received_condition_display}</td>
                  <td><StatusBadge status={repair.status} label={repair.status_display} /></td>
                  <td>{formatDate(repair.started_at)}</td>
                  <td>{formatDate(repair.completion_date || repair.completed_at)}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-secondary" type="button" onClick={() => setSelectedRepair(repair)}>View Details</button>
                      {repair.can_complete ? (
                        <button className="btn btn-primary" type="button" onClick={() => setCompletingRepair(repair)}>Complete Repair</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7}><EmptyTableState title="No repair records" description="No firearm repairs exist in this department scope." /></td></tr>
              )}
            </tbody>
          </table>
        </RecordsTableCard>
      )}
      {selectedReturnEvidence && (
        <DetailDrawer
          open
          title="Return Details and Evidence"
          subtitle={value(selectedReturnEvidence.firearm_serial)}
          onClose={() => setSelectedReturnEvidence(null)}
        >
          <div className="detail-grid">
            <div><span>Officer</span><strong>{value(selectedReturnEvidence.officer_name || selectedReturnEvidence.officer_badge)}</strong></div>
            <div><span>Return reason</span><strong>{titleText(selectedReturnEvidence.reason)}</strong></div>
            <div><span>Firearm condition</span><strong>{titleText(selectedReturnEvidence.firearm_condition)}</strong></div>
            <div className="full-width"><span>Remarks</span><p>{value(selectedReturnEvidence.remarks, 'No remarks')}</p></div>
          </div>
          {selectedReturnEvidence.legacy_evidence_warning ? (
            <div className="error-message" role="status">Legacy record without return-condition evidence. Confirmation remains available for compatibility.</div>
          ) : null}
          <FirearmEvidencePanel
            contextType="return"
            contextId={selectedReturnEvidence.id}
            firearmSerialNumber={selectedReturnEvidence.firearm_serial}
            readOnly
            required={!selectedReturnEvidence.legacy_evidence_warning}
            showTechnicalMetadata={false}
          />
          {canConfirm && (selectedReturnEvidence.status === 'submitted' || selectedReturnEvidence.status === 'pending') ? (
            <div className="form-actions">
              <button className="btn btn-primary" type="button" onClick={() => void confirm(selectedReturnEvidence.id)}>Confirm Return</button>
            </div>
          ) : null}
        </DetailDrawer>
      )}
      {selectedRepair && (() => {
        const issuance = issuances.find((item) => item.id === selectedRepair.previous_issuance);
        return issuance ? (
          <FirearmIssuanceDetailsDrawer
            issuance={issuance}
            repair={selectedRepair}
            onCompleteRepair={(repair) => setCompletingRepair(repair)}
            onClose={() => setSelectedRepair(null)}
          />
        ) : null;
      })()}
      {completingRepair ? (
        <CompleteRepairDialog
          repair={completingRepair}
          onClose={() => setCompletingRepair(null)}
          onCompleted={async (updated) => {
            setCompletingRepair(null);
            setSelectedRepair(updated);
            setMessage(updated.status === 'open' ? 'Repair follow-up recorded.' : 'Repair completed.');
            await Promise.all([load(), loadRepairs()]);
          }}
        />
      ) : null}
    </div>
  );
};

export const FirearmInventory = FirearmRegistry;
export const ReturnOversight = FirearmReturns;

export const ComplianceNotifications: React.FC = () => {
  // FRS 8.1-8.3: Logistics compliance/notification monitoring.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const canView = has('notification.system.view');
  const emptyPage: PaginatedResult<Notification> = {
    count: 0, next: null, previous: null, results: [],
  };
  const [pageData, setPageData] = React.useState<PaginatedResult<Notification>>(emptyPage);
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedNotification, setSelectedNotification] = React.useState<NotificationInboxItem | null>(null);
  const [feedback, setFeedback] = React.useState('');
  const [error, setError] = React.useState('');
  const load = React.useCallback(async () => {
    if (!canView) return;
    const filters = withAdminDepartmentParam({
      ...(activeFilter !== 'all' ? { category: activeFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(searchQuery ? { search: searchQuery } : {}),
      page,
    }, adminDepartmentId);
    setPageData(await notificationService.getSystemNotificationsPage(filters));
  }, [activeFilter, adminDepartmentId, canView, page, searchQuery, statusFilter]);
  React.useEffect(() => { void load().catch(() => setError('Notifications are temporarily unavailable.')); }, [load]);
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);
  const openNotification = (item: NotificationInboxItem) => {
    const previousStatus = item.status;
    const readItem = { ...item, status: 'read' };
    setSelectedNotification(readItem);
    setPageData((current) => ({
      ...current,
      results: current.results.map((notification) =>
        notification.id === item.id ? { ...notification, status: 'read' } : notification
      ),
    }));
    void markNotificationReadIfUnread(item.id, previousStatus)
      .catch(() => {
        setPageData((current) => ({
          ...current,
          results: current.results.map((notification) =>
            notification.id === item.id ? { ...notification, status: previousStatus } : notification
          ),
        }));
        setSelectedNotification((current) => (
          current?.id === item.id ? { ...current, status: previousStatus } : current
        ));
      });
  };
  const dismissSelectedNotification = async () => {
    if (!selectedNotification) return;
    try {
      await notificationService.dismissNotification(Number(selectedNotification.id));
      setSelectedNotification(null);
      setFeedback('Notification dismissed.');
      if (pageData.results.length === 1 && page > 1) {
        setPage((current) => current - 1);
        return;
      }
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Notification could not be dismissed.'));
    }
  };
  if (!canView) return <EmptyState title="Notifications unavailable" copy="This Logistics view requires notification.system.view." />;
  const inboxItems: NotificationInboxItem[] = pageData.results.map((notification) => ({
    id: notification.id,
    title: notificationTitle(notification),
    message: notificationMessage(notification),
    notification_type: notification.notification_type,
    severity: notification.severity,
    status: notification.status,
    officer_name: notification.officer_name,
    officer_badge: notification.officer_badge,
    related_context: complianceContext(notification),
    actionLabel: 'View Details',
    generated_at: notification.generated_at,
    created_at: notification.created_at,
    due_date: notification.due_date,
  }));

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Notifications</h1><p>Workflow, inspection, custody, and compliance notifications.</p></div></div>
      <FRSNote>FRS 8.1-8.3: Logistics Officer dashboard notifications and non-compliance flags.</FRSNote>
      <WorkflowFeedback message={feedback} onDismiss={() => setFeedback('')} />
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <NotificationInbox
        title="Notifications"
        items={inboxItems}
        emptyTitle="No notifications"
        emptyDescription="No Logistics notifications matched the current filters."
        onSelect={openNotification}
        toolbar={(
          <CompactTableToolbar
            className="notification-toolbar"
            searchValue={searchInput}
            searchPlaceholder="Search title, message, officer, firearm, department"
            searchLabel="Search notifications"
            onSearchChange={setSearchInput}
            filters={(
              <>
                <div className="segmented-tabs" role="tablist" aria-label="Notification filters">
                  {[
                    ['all', 'All'], ['compliance', 'Compliance'], ['issuance', 'Issuance'],
                    ['conflict', 'Conflict'], ['inspection', 'Inspection'],
                  ].map(([key, label]) => <button key={key} className={activeFilter === key ? 'active' : ''} onClick={() => { setPage(1); setActiveFilter(key); }} type="button">{label}</button>)}
                </div>
                <select aria-label="Notification status filter" value={statusFilter} onChange={(event) => { setPage(1); setStatusFilter(event.target.value); }}>
                  <option value="">All statuses</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </>
            )}
            resultCount={`${pageData.count} matching notification${pageData.count === 1 ? '' : 's'}`}
            actions={(
              <div className="notification-pagination">
                <button className="btn btn-secondary" type="button" disabled={!pageData.previous} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
                <span>Page {page}</span>
                <button className="btn btn-secondary" type="button" disabled={!pageData.next} onClick={() => setPage((current) => current + 1)}>Next</button>
              </div>
            )}
          />
        )}
      />
      {selectedNotification && (
        <DetailDrawer
          open={Boolean(selectedNotification)}
          title={selectedNotification.title || complianceLabel(selectedNotification.notification_type)}
          subtitle={selectedNotification.related_context}
          meta={<StatusBadge status={selectedNotification.status || 'notification'} label={titleText(selectedNotification.status)} />}
          onClose={() => setSelectedNotification(null)}
          closeLabel="Close notification details"
          footer={has('notification.system.dismiss') ? <button className="btn btn-secondary" type="button" onClick={dismissSelectedNotification}>Dismiss</button> : null}
        >
          <div className="detail-grid">
            <div><span>Type</span><strong>{complianceLabel(selectedNotification.notification_type)}</strong></div>
            <div><span>Status</span><strong>{selectedNotification.status === 'dismissed' ? 'Dismissed' : statusText(selectedNotification.status)}</strong></div>
            <div><span>Severity</span><strong>{statusText(selectedNotification.severity)}</strong></div>
            <div><span>Related officer</span><strong>{value(selectedNotification.officer_name || selectedNotification.officer_badge)}</strong></div>
            <div><span>Due date</span><strong>{formatDate(selectedNotification.due_date)}</strong></div>
            <div><span>Generated</span><strong>{formatDate(selectedNotification.generated_at || selectedNotification.created_at)}</strong></div>
            <div className="full-width"><span>Message</span><p>{sanitizeNotificationText(selectedNotification.message)}</p></div>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
};

export const LogisticsReports: React.FC = () => {
  // FRS 6.2: existing backend-supported Form 18 replica and Discrepancy/Reconciliation PDFs only.
  const [reports, setReports] = React.useState<any[]>([]);
  const [downloadingReportId, setDownloadingReportId] = React.useState<number | null>(null);
  const [error, setError] = React.useState('');
  React.useEffect(() => {
    inspectionService.getGeneratedReports()
      .then((items) => setReports(items.filter((report) => APPROVED_REPORT_TYPES.has(report.report_type))))
      .catch(() => setError('Generated reports are temporarily unavailable.'));
  }, []);
  const downloadReport = async (report: any) => {
    setDownloadingReportId(report.id);
    setError('');
    try {
      const blob = await inspectionService.downloadGeneratedReport(report.id);
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = report.filename || `generated-report-${report.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch {
      setError('Generated report download failed.');
    } finally {
      setDownloadingReportId(null);
    }
  };
  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Reports &amp; Monitoring</h1><p>Backend-supported discrepancy and inspection report monitoring.</p></div></div>
      <FRSNote>Form 18, Inspection and Compliance, and Reconciliation reports use the approved report-type contract.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <ValidationResults allowActions embedded custodyOnly />
      <section className="panel">
        {reports.length === 0 ? <EmptyState title="No generated reports" copy="No approved report files were returned." /> : (
          <div className="table-wrap"><table><thead><tr><th>Report</th><th>Filename</th><th>Status</th><th>Generated</th><th>Action</th></tr></thead><tbody>
            {reports.map((report) => <tr key={report.id}><td>{REPORT_TYPE_LABELS[report.report_type] || 'Generated Report'}</td><td>{value(report.filename)}</td><td>{statusText(report.status)}</td><td>{formatDate(report.generated_at)}</td><td><button className="btn btn-secondary" type="button" onClick={() => downloadReport(report)} disabled={downloadingReportId === report.id}><Download size={14} />{downloadingReportId === report.id ? 'Downloading...' : 'Download'}</button></td></tr>)}
          </tbody></table></div>
        )}
      </section>
    </div>
  );
};
